/**
 * TanStack Query hook for the subscription-status slice.
 *
 * Replaces the server half of the legacy `usePaymentStore` Zustand
 * store, which mixed fetched subscription state
 * (`hasActiveSubscription`, `currentPlan`, `billingCycle`, `canceled`)
 * with persisted client UI state (`selectedPlan`,
 * `selectedBillingCycle`). The two halves had different lifecycles —
 * server data needs invalidation + refetching; client UI state needs
 * localStorage persistence — so we split them.
 *
 * Patterns mirror the visibility-domain hooks under `hooks/visibility/`:
 *
 * - The fetch + shape mapping live inside `queryFn`. We normalize the
 *   snake_case API response (`has_active_subscription`, `plan_type`,
 *   `billing_cycle`) into a camelCase `Subscription` consumer shape.
 *
 * - The key is `subscriptionKeys.detail(orgId ?? "current")` so calls
 *   that don't know the active org (the common case — most UI just
 *   asks "is this user subscribed?") share a single cache slot keyed
 *   by `"current"`. The API resolves the user's primary org server-
 *   side from the auth cookie.
 *
 * - `enabled: true` — we don't gate on a slug like the visibility
 *   hooks do because the subscription-status endpoint is the source
 *   of truth for "is this user on a paid plan?" and gets called from
 *   public-app-shell surfaces (UpgradeButton, etc.) that don't have a
 *   project context.
 *
 * - Returns the raw `UseQueryResult`. Consumers destructure
 *   `{ data, isPending, isError, ... }` directly. The legacy store
 *   exposed a `_hasHydrated` boolean to gate UI during localStorage
 *   rehydration; that's no longer needed for subscription data
 *   because `isPending` covers the same "waiting for the answer"
 *   state with a cleaner discriminated-union shape.
 *
 * - Cross-component invalidation (e.g. after a checkout completes,
 *   after a subscription is canceled in the Stripe portal) goes
 *   through `invalidateBilling(qc, subscriptionKeys.detailPrefix(orgId))`,
 *   not `qc.invalidateQueries` directly, to keep the prefix-vs-exact
 *   typing constraint enforced at compile time.
 */

import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { subscriptionKeys } from "@/lib/queryKeys";
import { getSubscriptionStatus } from "@/services/billingApi";
import type { BillingCycle, PlanType } from "@/stores/paymentSelection/types";

/**
 * The org a billing surface acts on. Omitted means the caller's
 * personal org (the server's default for every billing endpoint).
 */
export interface BillingOrgRef {
  id: string;
  slug: string;
}

export interface Subscription {
  hasActiveSubscription: boolean;
  currentPlan: PlanType;
  billingCycle: BillingCycle | null;
  canceled: boolean | null;
}

async function fetchSubscription(orgId?: string): Promise<Subscription> {
  const status = await getSubscriptionStatus(orgId);

  // `getSubscriptionStatus` swallows fetch errors and returns null on
  // failure so the call site never throws. Treat that as the
  // free-tier default — same fall-back the legacy store used in its
  // catch block.
  if (!status) {
    return {
      hasActiveSubscription: false,
      currentPlan: "free",
      billingCycle: null,
      canceled: false,
    };
  }

  return {
    hasActiveSubscription: Boolean(status.has_active_subscription),
    currentPlan: (status.plan_type as PlanType | undefined) ?? "free",
    billingCycle: status.billing_cycle ?? null,
    canceled: status.canceled ?? false,
  };
}

export function useSubscription(
  orgId?: string,
): UseQueryResult<Subscription, Error> {
  return useQuery({
    queryKey: subscriptionKeys.detail(orgId ?? "current"),
    queryFn: () => fetchSubscription(orgId),
  });
}
